import { HttpClient } from '@adapters/HttpClient';
import {
  Rate,
  RatesFilters,
  RatesApiResponse,
  RatePage,
  CreateRateRequest,
  CreateRateItemRequest,
  RateItem,
} from '../models/rate.types';

export class RatesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getRatesPaginated(filters: RatesFilters = {}): Promise<RatePage> {
    const { search, is_active, page = 1, per_page = 20, sorting } = filters;

    const params: Record<string, unknown> = {
      page,
      per_page,
    };

    // Add sorting if provided
    if (sorting) {
      params['sorting[column]'] = sorting.column;
      params['sorting[direction]'] = sorting.direction;
    }

    // Build advanced_filters array
    let filterIndex = 0;
    const hasSearch = search && search.trim().length > 0;
    const hasStatusFilter = is_active !== undefined && is_active !== 'all';

    // Add search filter via advanced_filters (API doesn't support search param)
    if (hasSearch) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'name';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'contains';
      params[`advanced_filters[filters][${filterIndex}][value]`] = search.trim();
      filterIndex++;
    }

    // Add is_active filter via advanced_filters
    if (hasStatusFilter) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'is_active';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'in';
      params[`advanced_filters[filters][${filterIndex}][value]`] = [is_active ? 1 : 0];
      filterIndex++;
    }

    // IMPORTANT: Always include logic when using advanced_filters (backend bug workaround)
    if (hasSearch || hasStatusFilter) {
      params['advanced_filters[logic]'] = 'AND';
    }

    // Show global loading only on first page
    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<RatesApiResponse>('/rates', params, {}, showGlobalLoading);

    return {
      rates: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getRateById(id: number, showGlobalLoading = true): Promise<Rate> {
    const response = await this.httpClient.get<Rate>(`/rates/${id}`, undefined, {}, showGlobalLoading);
    return response;
  }

  async createRate(data: CreateRateRequest): Promise<Rate> {
    const response = await this.httpClient.post<Rate>('/rates', data, true);
    return response;
  }

  async addRateItem(rateId: number, data: CreateRateItemRequest): Promise<RateItem> {
    const response = await this.httpClient.post<RateItem>(`/rates/${rateId}/items`, data, true);
    return response;
  }

  async changeRateStatus(rateId: number): Promise<void> {
    await this.httpClient.put<void>(`/rates/${rateId}/change-active`, {});
  }
}
